import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { uniqueId } from 'lodash';
import classNames from 'classnames';
import { Button } from 'reactstrap';
import RubricQuestion from './RubricQuestion';

import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import { useRubricContext } from './RubricContext';
import { Icon } from 'js/components';
import {
  faExclamationTriangle,
  faWeightHanging,
  faGripVertical,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { UncontrolledTooltip } from 'reactstrap';
import ErrorMessage from 'js/components/Assessment/Editors/common/components/EditorErrors/ErrorMessage';
import DropIndicator from 'js/components/Assessment/Editors/common/components/DropIndicator';
import EditorInput from './EditorInput';
import RubricQuestionPickerModal from './RubricQuestionPickerModal';
import CategorySelector from 'js/components/Assessment/Editor/QuestionSets/CategorySelector';
import RubricMeasurementActions from './RubricMeasurementActions';
import styles from './RubricEditor.module.scss';

const categoryCollator = new Intl.Collator(undefined, {
  sensitivity: 'base',
});

function QuestionSetDragPreview({ questionSet, width }) {
  return (
    <div
      className={classNames(styles['question-set'], 'drag-preview')}
      style={{ width }}
    >
      <div className={styles['panel-header']}>
        <div className={styles['drag-handle']}>
          <span className="drag-handle">
            <Icon icon={faGripVertical} className="icon" />
          </span>
        </div>
        <div className={styles['question-set-weight']}>
          <span title="Weight">
            <Icon icon={faWeightHanging} className="icon icon-left text-info" />
            {questionSet.weight}
          </span>
        </div>
        <div className={styles.key}>{questionSet.key}</div>
        <div className={styles['question-set-category-preview']}>
          {String(questionSet.category ?? '').trim() || '—'}
        </div>
      </div>
      <div className={styles['question-set-header']}>
        <div className={styles['question-set-name']}>{questionSet.name}</div>
      </div>
    </div>
  );
}

function RubricQuestionSet({ questionSet, questionSetIndex }) {
  const guidRef = useRef(uniqueId('rqs-'));
  const rootRef = useRef(null);
  const dragHandleRef = useRef(null);
  const emptyDropRef = useRef(null);

  const {
    rubric,
    updateQuestionSetField,
    addRubricQuestionsFromSources,
    errors,
    moveRubricQuestionSetBefore,
    moveRubricQuestionSetAfter,
    moveRubricQuestionToEnd,
    removeRubricQuestionSet,
    removeRubricQuestion,
    setRubricQuestionSetRetiredAt,
  } = useRubricContext();

  const questionSetGuid = questionSet.guid;
  const questions = questionSet.questions ?? [];
  const isEmpty = questions.length === 0;

  const [closestEdgeSet, setClosestEdgeSet] = useState(null);
  const [isDraggedOverSet, setIsDraggedOverSet] = useState(false);
  const [isEmptyDropOver, setIsEmptyDropOver] = useState(false);
  const [showQuestionPicker, setShowQuestionPicker] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const handleFieldChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateQuestionSetField(questionSetGuid, name, value);
    },
    [questionSetGuid, updateQuestionSetField]
  );

  const handleFieldBlur = useCallback(
    (e) => {
      const { name } = e.target;
      const current = e.target.value;
      if (name === 'weight') {
        const parsed = parseFloat(String(current).replace(/[^0-9.-]/g, ''));
        const next = Number.isFinite(parsed) ? parsed : 1;
        updateQuestionSetField(questionSetGuid, name, next);
        return;
      }
      const next = String(current ?? '').trim();
      if (next !== current) {
        e.target.value = next;
      }
      updateQuestionSetField(questionSetGuid, name, next);
    },
    [questionSetGuid, updateQuestionSetField]
  );

  const fieldErrors = errors.questionSets[questionSetGuid] ?? {};
  const keyError = fieldErrors.key;

  const categoryList = useMemo(() => {
    const sets = rubric?.document?.question_sets ?? [];
    const names = new Set();
    for (const qs of sets) {
      const c = String(qs.category ?? '').trim();
      if (c) {
        names.add(c);
      }
    }
    const sorted = [...names].sort((a, b) => categoryCollator.compare(a, b));
    return sorted.map((display_name, i) => ({
      key: display_name,
      display_name,
      colorIndex: i % 12,
    }));
  }, [rubric?.document?.question_sets]);

  const categoryForSelector = useMemo(() => {
    const raw = String(questionSet.category ?? '').trim();
    if (!raw) {
      return null;
    }
    const match = categoryList.find(
      (c) => categoryCollator.compare(c.display_name, raw) === 0
    );
    return match ?? { key: raw, display_name: raw };
  }, [questionSet.category, categoryList]);

  const handleCategoryChanged = useCallback(
    (val) => {
      let next = val;
      if (next != null) {
        next = String(next).trim();
      }
      const uncategorized =
        next == null ||
        next === '' ||
        categoryCollator.compare(next, 'Uncategorized') === 0 ||
        categoryCollator.compare(next, 'uncategorized') === 0;
      updateQuestionSetField(
        questionSetGuid,
        'category',
        uncategorized ? '' : next
      );
    },
    [questionSetGuid, updateQuestionSetField]
  );

  const handleAddQuestions = useCallback(
    (selectedSources) => {
      addRubricQuestionsFromSources(questionSetGuid, selectedSources, {
        includeQuestionSets: false,
      });
    },
    [addRubricQuestionsFromSources, questionSetGuid]
  );

  const handleAddQuestionsWithSets = useCallback(
    (selectedSources) => {
      addRubricQuestionsFromSources(questionSetGuid, selectedSources, {
        includeQuestionSets: true,
      });
    },
    [addRubricQuestionsFromSources, questionSetGuid]
  );

  useEffect(() => {
    const el = rootRef.current;
    const dragHandle = dragHandleRef.current;
    if (!el || !dragHandle) {
      return undefined;
    }

    return combine(
      draggable({
        element: el,
        dragHandle,
        getInitialData: () => ({
          rubricQuestionSet: questionSet,
          questionSetIndex,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            render({ container }) {
              ReactDOM.render(
                <QuestionSetDragPreview
                  questionSet={questionSet}
                  width={el.scrollWidth}
                />,
                container
              );
              return function cleanup() {
                ReactDOM.unmountComponentAtNode(container);
              };
            },
            getOffset: () => ({ x: 20, y: 16 }),
            nativeSetDragImage,
          });
        },
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) =>
          !!(
            source.data.rubricQuestionSet &&
            source.data.rubricQuestionSet.guid !== questionSet.guid
          ),
        getData: (args) =>
          attachClosestEdge(
            { rubricQuestionSet: questionSet, questionSetIndex },
            {
              input: args.input,
              element: args.element,
              allowedEdges: ['top', 'bottom'],
            }
          ),
        onDragEnter: () => setIsDraggedOverSet(true),
        onDragLeave: () => {
          setIsDraggedOverSet(false);
          setClosestEdgeSet(null);
        },
        onDrag({ self, source }) {
          const isSource = source.element === el;
          if (isSource || !source.data.rubricQuestionSet) {
            setClosestEdgeSet(null);
            return;
          }
          setClosestEdgeSet(extractClosestEdge(self.data));
        },
        onDrop: ({ source, location }) => {
          setIsDraggedOverSet(false);
          setClosestEdgeSet(null);
          if (location.current.dropTargets.length > 1) {
            return;
          }
          const dragged = source.data.rubricQuestionSet;
          if (!dragged || dragged.guid === questionSet.guid) {
            return;
          }
          const dropTarget = location.current.dropTargets[0];
          const edge = extractClosestEdge(dropTarget.data);
          if (edge === 'top') {
            moveRubricQuestionSetBefore(dragged.guid, questionSet.guid);
          } else if (edge === 'bottom') {
            moveRubricQuestionSetAfter(dragged.guid, questionSet.guid);
          }
        },
      })
    );
  }, [
    questionSet,
    questionSetIndex,
    moveRubricQuestionSetBefore,
    moveRubricQuestionSetAfter,
  ]);

  useEffect(() => {
    const zone = emptyDropRef.current;
    if (!zone || !isEmpty) {
      return undefined;
    }

    return dropTargetForElements({
      element: zone,
      getData: () => ({ rubricEmptyQuestionsFor: questionSetGuid }),
      canDrop: ({ source }) => !!source.data.rubricQuestion,
      onDragEnter: () => setIsEmptyDropOver(true),
      onDragLeave: () => setIsEmptyDropOver(false),
      onDrop: ({ source }) => {
        setIsEmptyDropOver(false);
        const rq = source.data.rubricQuestion;
        const fromSet = source.data.questionSetGuid;
        if (!rq || !fromSet) {
          return;
        }
        moveRubricQuestionToEnd(rq.guid, fromSet, questionSetGuid);
      },
    });
  }, [isEmpty, questionSetGuid, moveRubricQuestionToEnd]);

  const rootClassName = useMemo(
    () =>
      classNames(styles['question-set'], {
        [styles['question-set-drag-over']]: isDraggedOverSet,
      }),
    [isDraggedOverSet]
  );

  return (
    <div className={rootClassName} ref={rootRef}>
      {isDraggedOverSet && closestEdgeSet !== null && (
        <DropIndicator
          top={closestEdgeSet === 'top'}
          bottom={closestEdgeSet === 'bottom'}
        />
      )}
      <div className={styles['panel-header']}>
        <button
          type="button"
          className="drag-handle"
          ref={dragHandleRef}
          aria-label="Drag to reorder question set"
        >
          <Icon icon={faGripVertical} className="icon" />
        </button>
        <div className={styles['question-set-weight']}>
          <EditorInput
            name="weight"
            value={questionSet.weight}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            convertToNumber
            defaultNumber={1}
            placeholder="weight"
          >
            <span title="Weight">
              <Icon
                icon={faWeightHanging}
                className="icon icon-left text-info"
              />
              {questionSet.weight}
            </span>
          </EditorInput>
        </div>
        <div className={styles.key}>
          <EditorInput
            name="key"
            value={questionSet.key}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            placeholder="question-set-key"
          />
        </div>
        {keyError && (
          <div className={styles.keyError} id={`${guidRef.current}-key`}>
            <Icon
              icon={faExclamationTriangle}
              className="icon icon-left text-danger"
            />
            <UncontrolledTooltip
              color="danger"
              placement="top"
              target={`#${guidRef.current}-key`}
              className={styles.tooltip}
              trigger="click hover"
              autohide={false}
            >
              <ErrorMessage>{keyError}</ErrorMessage>
            </UncontrolledTooltip>
          </div>
        )}
        <div className={styles.categorySelector}>
          <CategorySelector
            category={categoryForSelector}
            categories={categoryList}
            onChange={handleCategoryChanged}
          />
        </div>
      </div>
      <div className={styles['question-set-header']}>
        <div className={styles['question-set-name']}>
          <EditorInput
            name="name"
            value={questionSet.name}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={
              fieldErrors.name ? (
                <ErrorMessage>{fieldErrors.name}</ErrorMessage>
              ) : null
            }
            placeholder="question-set-name"
          />
        </div>
      </div>
      <div className={styles['question-set-footer']}>
        <div>
          <button
            type="button"
            className={classNames(styles.expander, {
              [styles.collapsed]: !expanded,
            })}
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Collapse questions' : 'Expand questions'}
          >
            <Icon icon={faChevronDown} className="icon icon-left" />
            {!expanded
              ? `${questions.length} ${
                  questions.length === 1 ? 'question' : 'questions'
                }`
              : null}
          </button>
        </div>
        <div className={styles['question-set-actions']}>
          <RubricMeasurementActions
            ariaLabel="Remove question set"
            modalTitle="Remove question set?"
            detailPrimary={questionSet.key}
            detailSecondary={questionSet.name}
            deleteNote="This will also remove all questions inside it."
            retiredAt={questionSet.retired_at}
            retireDescription="Stop counting this question set toward scores after the retirement date."
            onRetire={(date) =>
              setRubricQuestionSetRetiredAt(questionSetGuid, date)
            }
            onClear={() => setRubricQuestionSetRetiredAt(questionSetGuid, null)}
            onDelete={() => removeRubricQuestionSet(questionSetGuid)}
          />
        </div>
      </div>
      <div
        className={classNames(
          styles['question-set-questions'],
          { [styles.collapsed]: !expanded },
          'question-set-droppable-area'
        )}
      >
        {expanded && isEmpty && (
          <div
            ref={emptyDropRef}
            className={styles['empty-questions-drop']}
            style={{ position: 'relative', minHeight: 48 }}
          >
            {isEmptyDropOver && <DropIndicator />}
          </div>
        )}
        {expanded &&
          questions.map((question, index) => (
            <RubricQuestion
              key={question.guid ?? index}
              questionSetGuid={questionSetGuid}
              questionIndex={index}
              question={question}
              questionSetRetired={!!questionSet.retired_at}
              onRequestRemoveQuestion={removeRubricQuestion}
            />
          ))}
        {expanded && !questionSet.retired_at && (
          <div className={styles['question-set-action-list']}>
            <Button
              type="button"
              color="primary"
              className={styles['question-set-add-question']}
              outline
              onClick={() => setShowQuestionPicker(true)}
            >
              <Icon icon={faPlus} className="btn-icon-left" />
              Add question
            </Button>
          </div>
        )}
      </div>
      <RubricQuestionPickerModal
        isOpen={showQuestionPicker}
        onClose={() => setShowQuestionPicker(false)}
        onAddQuestions={handleAddQuestions}
        onAddQuestionsWithSets={handleAddQuestionsWithSets}
      />
    </div>
  );
}

export default RubricQuestionSet;
